import { ELSDropDownOption } from '@els/els-component-form-field-react';
import {
  ActivityTypeMetricsDto,
  QuestionSubmissionDetailsDto,
  QuizAttemptDetailsDto,
  RaasAssessmentDto,
  RaasAssessmentSourceDto,
  RaasAssessmentSourceTypeDto,
  RecommendationAttemptDto,
  RecommendationAttemptStatusDto,
  RecommendationContentTypeDto,
  RecommendationDto,
  RemediationActivityTypeDto,
  RemediationActivityTypeSettings,
  RemediationAssignmentSourceTypeDto,
  RemediationAssignmentWithSourcesDto,
  StatusWithDueDate,
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  AssessmentGoalDto,
  AssessmentSubmissionDto
} from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import {
  ActivityTypeConfigMap,
  RemediationContentTypeConfigMap,
} from '../../models/remediation.models';
import {
  convertSecondsToMinutes,
  getAssessmentSubmissionsFromAttempt,
  getAttemptsFromRecommendation,
  getAttemptStatus,
  getHourAndMinuteFromSecond,
  getQuizAttempt,
  getRecommendationFromAttempt,
  getRecommendationsFromGoal,
  getRecommendationStatusWithDueDate
} from '../../utilities/remediation.utilities';
import { EmptyValueDisplay } from './remediation-student-activity.constants';
import {
  ActivityLogTableData,
  ActivityProgressApplyScoreFilter
} from './remediation-student-activity.models';
import { RemediationBaseState } from '../../hocs/with-remediation-base/withRemediationBase.hoc';
import { StatusPill } from '../../components/status-pill/StatusPill.component';
import { StatusDisplayMap } from '../../constants/remediation.constants';
import { UserDto } from '../../apis/eols-user-management-service/eols-user-management-service.dtos';

export const getActivityTypeSettings = (
  assignment: RemediationAssignmentWithSourcesDto,
  activityType: RemediationActivityTypeDto
): RemediationActivityTypeSettings => {
  if (!assignment?.remediationAssignment?.settings) {
    return null;
  }

  return assignment.remediationAssignment.settings.activityTypes.find((activitySettings) => activitySettings.activityType === activityType);
};

export const getActivityTypeTitle = (activityType: RemediationActivityTypeDto, contentTypes: string): string => {
  return `${ActivityTypeConfigMap[activityType].title}: ${contentTypes}`;
};

export const getTaxonSubtitle = (
  assessmentGoals: AssessmentGoalDto[],
  activityType: RemediationActivityTypeDto,
  metricCounts: ActivityTypeMetricsDto,
  contentTypes: string
): string => {
  if (!metricCounts) {
    return null;
  }
  const numTopics = assessmentGoals.filter(goal => goal.type === activityType).length;
  const determinePlural = numTopics > 1 ? 's' : '';
  return `Requirement: ${metricCounts.activityTypeTotalGoal} ${contentTypes} across ${numTopics} topic${determinePlural}`;
};

export const getContentTypeSubtitle = (
  activityTypeSettings: RemediationActivityTypeSettings,
  activityType: RemediationActivityTypeDto,
  metricCounts: ActivityTypeMetricsDto,
  contentTypes: string
): string => {
  if (
    !activityTypeSettings ||
    !activityTypeSettings.contentSettings ||
    activityTypeSettings.contentSettings.length === 0
  ) {
    return null;
  }

  if (!metricCounts) {
    return null;
  }

  const [contentSetting] = activityTypeSettings.contentSettings;
  const minScoreToPass = contentSetting.minScoreToPass
    ? contentSetting.minScoreToPass * 100
    : null;

  if (activityType === RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE) {
    const correctQuestions = metricCounts.activityTypeTotalGoal;
    const subtitle = minScoreToPass ? `with at least ${correctQuestions} correct (based on ${minScoreToPass}% correct goal in settings)` : '';
    const attemptedQuestions = metricCounts.activityTypeAttemptGoal;
    return `Requirement: ${attemptedQuestions} attempted quiz questions ${subtitle}`;
  }

  const subtitle = minScoreToPass ? `with a score of ${minScoreToPass}%` : '';

  return `Requirement: ${metricCounts.activityTypeTotalGoal} ${contentTypes} ${subtitle}`;
};

export const getRequirementSubtitle = (
  remediationAssignmentWithSources: RemediationAssignmentWithSourcesDto,
  assessmentGoals: AssessmentGoalDto[],
  activityType: RemediationActivityTypeDto,
  metricCounts: ActivityTypeMetricsDto,
  contentTypes: string,
): string => {
  if (!activityType || !assessmentGoals) {
    return null;
  }

  const lowerCaseContentTypesDisplay = contentTypes.toLowerCase();

  const activityTypeSettings = getActivityTypeSettings(remediationAssignmentWithSources, activityType);

  if (activityType === RemediationActivityTypeDto.REVIEW_BY_TAXON) {
    return getTaxonSubtitle(assessmentGoals, activityType, metricCounts, lowerCaseContentTypesDisplay);
  }

  if (activityType === RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE || activityType === RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE) {
    return getContentTypeSubtitle(activityTypeSettings, activityType, metricCounts, lowerCaseContentTypesDisplay);
  }

  return null;
};

export const getAssessmentSubmissionsForAttempts = (attempts: RecommendationAttemptDto[], attemptSubmissions: AssessmentSubmissionDto[]): AssessmentSubmissionDto[] => {
  return attempts.flatMap((attempt) => {
    if (!attempt) {
      return null;
    }

    return getAssessmentSubmissionsFromAttempt(attempt.eolsAssessmentId, attemptSubmissions);
  });
};

export const getSubmissionsTotalTimeSpent = (submissions: AssessmentSubmissionDto[]): number => {
  if (!submissions || submissions.length === 0) {
    return 0;
  }

  return submissions.reduce((total, submission) => {
    if (!submission) {
      return total;
    }

    return total + (submission.timeSpent || 0);
  }, 0);
};

export const getRecTimeSpent = (
  recommendation: RecommendationDto,
  recommendationAttempts: RecommendationAttemptDto[],
  attemptSubmissions: AssessmentSubmissionDto[]
): number => {
  const attempts = getAttemptsFromRecommendation(recommendation.id, recommendationAttempts);

  if (!attempts || attempts.length === 0) {
    return 0;
  }

  const submissions = getAssessmentSubmissionsForAttempts(attempts, attemptSubmissions);

  return getSubmissionsTotalTimeSpent(submissions);
};

export const getCompletedAttempts = (recommendations: RecommendationDto[], recommendationAttempts: RecommendationAttemptDto[]): RecommendationAttemptDto[] => {
  if (!recommendationAttempts || !recommendationAttempts.length) {
    return null;
  }

  return recommendationAttempts.filter((attempt) => {
    const recommendation = recommendations.find((rec) => {
      return rec.id === attempt.recommendationId;
    });

    if (!recommendation) {
      return null;
    }

    return attempt.status !== RecommendationAttemptStatusDto.NOT_STARTED && attempt.status !== RecommendationAttemptStatusDto.IN_PROGRESS;
  });
};

export const getQuestionSubmissionDetailsForAttempt = (
  recommendationAttempt: RecommendationAttemptDto,
  quizAttemptDetails: QuizAttemptDetailsDto
): QuestionSubmissionDetailsDto[] => {
  if (!recommendationAttempt || !recommendationAttempt.id || !quizAttemptDetails) {
    return null;
  }

  return quizAttemptDetails.questionSubmissionDetails.filter((submission) => {
    return submission.recommendationAttemptId === recommendationAttempt.id && submission.questions && submission.questions.length > 0;
  });
};

export const getCorrectQuestionCount = (questionSubmissionDetail: QuestionSubmissionDetailsDto): number => {
  if (!questionSubmissionDetail || !questionSubmissionDetail.questions) {
    return 0;
  }

  return questionSubmissionDetail.questions.filter((question) => {
    return question.correct;
  }).length;
};

export const getLastDateFromQuestionSubmissionDetail = (questionSubmissionDetail: QuestionSubmissionDetailsDto): string => {
  if (!questionSubmissionDetail || !questionSubmissionDetail.questions) {
    return null;
  }

  const lastQuestion = questionSubmissionDetail.questions[questionSubmissionDetail.questions.length - 1];
  if (!lastQuestion || !lastQuestion.responseTime) {
    return null;
  }
  return lastQuestion.responseTime.replace('GMT', 'Z');
};

export const formatSubmittedDate = (submittedDate: string): string => {
  if (!submittedDate) {
    return null;
  }

  const date = new Date(submittedDate);

  const dateString = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .replace(/\//g, '-');

  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${dateString} ${timeString}`;
};

export const getRecActivityType = (attempt: RecommendationAttemptDto, recommendations: RecommendationDto[]): string => {
  const recommendation = getRecommendationFromAttempt(recommendations, attempt);

  return ActivityTypeConfigMap[recommendation.assessmentGoalType].title;
};

export const getActivityTimeSpentSeconds = (activity: ActivityLogTableData, attemptAssessmentSubmissions: AssessmentSubmissionDto[]): number => {
  if (!activity) return null;

  if (activity.recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
    const submissions = getAssessmentSubmissionsFromAttempt(activity.recommendationAttempt.eolsAssessmentId, attemptAssessmentSubmissions);

    return getSubmissionsTotalTimeSpent(submissions);
  }

  const { questionSubmissionDetail } = activity;

  return questionSubmissionDetail.questions.reduce((total, question) => {
    if (!question.timeSpent) {
      return total;
    }

    return total + question.timeSpent;
  }, 0);

};

export const getSubmittedDate = (activity: ActivityLogTableData): string => {
  if (!activity) return null;

  if (activity.recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
    return formatSubmittedDate(activity.recommendationAttempt.updatedAt);
  }

  return formatSubmittedDate(getLastDateFromQuestionSubmissionDetail(activity.questionSubmissionDetail));
};

export const filterActivityBySelectedActivityType = (activityRows: ActivityLogTableData[], selectedActivityTypes: RemediationActivityTypeDto[]): ActivityLogTableData[] => {
  if (!activityRows || !activityRows.length) {
    return [];
  }

  if (!selectedActivityTypes || !selectedActivityTypes.length) {
    return activityRows;
  }
  return activityRows.filter((row) => {
    return selectedActivityTypes.includes(row.recommendation.assessmentGoalType);
  });
};

export const filterActivityBySelectedContentType = (activityRows: ActivityLogTableData[], selectedContentTypes: RecommendationContentTypeDto[]): ActivityLogTableData[] => {
  if (!activityRows || !activityRows.length) {
    return [];
  }

  if (!selectedContentTypes || !selectedContentTypes.length) {
    return activityRows;
  }
  return activityRows.filter((row) => {
    return selectedContentTypes.includes(row.recommendation.contentType);
  });
};

export const filterActivityBySelectedStatus = (
  activityRows: ActivityLogTableData[],
  baseState: RemediationBaseState,
  selectedStatuses: StatusWithDueDate[]
): ActivityLogTableData[] => {
  if (!activityRows || !activityRows.length) {
    return [];
  }

  if (!selectedStatuses || !selectedStatuses.length) {
    return activityRows;
  }

  return activityRows.reduce<ActivityLogTableData[]>((acc, row) => {
    const status = getAttemptStatus(row.recommendationAttempt, baseState);
    if (selectedStatuses.includes(status)) {
      acc.push(row);
    }
    return acc;
  }, []);
};

export const filterActivityBySelectedGoal = (activityRows: ActivityLogTableData[], vtwId: string): ActivityLogTableData[] => {
  if (!activityRows || !activityRows.length) {
    return [];
  }

  if (!vtwId) {
    return activityRows;
  }
  return activityRows.filter((row) => {
    return row.recommendation.assessmentGoalVtwId === vtwId;
  });
};

export const filterActivityByActivityName = (
  activityRows: ActivityLogTableData[],
  selectedGoalId: string,
): ActivityLogTableData[] => {
  if (!activityRows || !activityRows.length) {
    return [];
  }

  if (!selectedGoalId) {
    return activityRows;
  }

  return filterActivityBySelectedGoal(activityRows, selectedGoalId);
};

export const getRecommendationsForAllGoals = (recommendations: RecommendationDto[], goals: AssessmentGoalDto[]): RecommendationDto[] => {
  if (!goals || !goals.length || !recommendations || !recommendations.length) {
    return [];
  }

  return goals.reduce<RecommendationDto[]>((acc, goal) => {
    const goalRecommendations = getRecommendationsFromGoal(recommendations, goal);

    if (!goalRecommendations || !goalRecommendations.length) {
      return acc;
    }

    return [...acc, ...goalRecommendations];
  }, []);
};

export const isInRange = (props: {
  date: Date;
  startDate: Date;
  endDate: Date;
}): boolean => {
  const {
    date,
    startDate,
    endDate
  } = props;

  if (startDate && endDate) {
    return date >= startDate && date <= endDate;
  }
  if (startDate) {
    return date >= startDate;
  }
  if (endDate) {
    return date <= endDate;
  }
  return true;
};

export const filterByDateRange = (
  activityRows: ActivityLogTableData[],
  startDate: Date,
  endDate: Date,
  baseState: RemediationBaseState
): ActivityLogTableData[] => {
  if (!activityRows || !activityRows.length) {
    return [];
  }

  if (!startDate && !endDate) {
    return activityRows;
  }

  return activityRows.filter((row) => {
    const attemptDate = row.recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ
      ? new Date(row.recommendationAttempt.updatedAt)
      : new Date(getLastDateFromQuestionSubmissionDetail(row.questionSubmissionDetail));

    const isInProgress = row.recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ
      ? isRecommendationInProgress(row.recommendation, baseState)
      : false;

    return !isInProgress && isInRange({
      date: attemptDate,
      startDate,
      endDate
    });
  });
};

export const getQuizSubmissionCount = (
  recommendations: RecommendationDto[],
  quizAttemptDetails: QuizAttemptDetailsDto,
  attemptsInActivityType: RecommendationAttemptDto[]
): number => {
  if (!recommendations || !recommendations.length || !quizAttemptDetails || !quizAttemptDetails.questionSubmissionDetails) {
    return 0;
  }

  const quizAttempt = attemptsInActivityType.find((attempt) => {
    const recommendation = getRecommendationFromAttempt(recommendations, attempt);
    return recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ;
  });
  const quizSubmissions = getQuestionSubmissionDetailsForAttempt(quizAttempt, quizAttemptDetails);

  return quizSubmissions?.length ? quizSubmissions.length : 0;
};

export const getGoalTimeSpentInMinutes = (props: {
  recommendations: RecommendationDto[];
  recommendationAttempts: RecommendationAttemptDto[];
  attemptAssessmentSubmissions: AssessmentSubmissionDto[];
  assessmentGoal: AssessmentGoalDto;
}): number => {
  const {
    recommendations,
    recommendationAttempts,
    attemptAssessmentSubmissions,
    assessmentGoal
  } = props;

  const goalRecommendations = getRecommendationsFromGoal(recommendations, assessmentGoal);

  return goalRecommendations.reduce((total, recommendation) => {
    if (!recommendation) {
      return total;
    }

    // We are calculating to the minute instead of seconds to more accurately match the sum of individual minutes shown in the Activity Log and recommendation detail table
    // for the respective goal
    return total + convertSecondsToMinutes(getRecTimeSpent(recommendation, recommendationAttempts, attemptAssessmentSubmissions));
  }, 0);
};

export const sortAssessmentSubmissionsOldestToLatest = (submissions: AssessmentSubmissionDto[]): AssessmentSubmissionDto[] => {
  if (!submissions || submissions.length < 1) {
    return [];
  }

  return submissions.sort((a, b) => {
    if (!a || !b) {
      return 0;
    }

    const aCreatedAt = new Date(a.createdAt);
    const bCreatedAt = new Date(b.createdAt);

    return aCreatedAt.getTime() - bCreatedAt.getTime();
  });
};

export const getFirstCompletedAttempt = (props: {
  recommendationAttempts: RecommendationAttemptDto[];
  attemptAssessmentSubmissions: AssessmentSubmissionDto[];
  recommendation: RecommendationDto;
}): RecommendationAttemptDto => {
  const {
    recommendationAttempts,
    attemptAssessmentSubmissions,
    recommendation
  } = props;

  const attempts = getCompletedAttempts([recommendation], recommendationAttempts);

  if (!attempts || attempts.length === 0) {
    return null;
  }

  const submissions = sortAssessmentSubmissionsOldestToLatest(getAssessmentSubmissionsForAttempts(attempts, attemptAssessmentSubmissions));

  if (!submissions || submissions.length === 0) {
    return null;
  }

  return attempts.find((attempt) => {
    return attempt.eolsAssessmentId === submissions[0].assessmentId;
  });
};

export const getBestCompletedAttempt = (props: {
  recommendationAttempts: RecommendationAttemptDto[];
  attemptAssessmentSubmissions: AssessmentSubmissionDto[];
  recommendation: RecommendationDto;
}): RecommendationAttemptDto => {
  const {
    recommendationAttempts,
    attemptAssessmentSubmissions,
    recommendation
  } = props;

  const attempts = getCompletedAttempts([recommendation], recommendationAttempts);

  if (!attempts || attempts.length === 0) {
    return null;
  }

  const submissions = sortAssessmentSubmissionsOldestToLatest(getAssessmentSubmissionsForAttempts(attempts, attemptAssessmentSubmissions));

  if (!submissions || submissions.length === 0) {
    return null;
  }

  const bestSubmission = submissions.reduce((best, submission) => {
    if (!submission) {
      return best;
    }

    if (!best || submission.score > best.score) {
      return submission;
    }
    return best;
  }, null);

  if (!bestSubmission) {
    return null;
  }

  return attempts.find((attempt) => {
    return attempt.eolsAssessmentId === bestSubmission.assessmentId;
  });
};

export const getAttemptToViewByFilter = (props: {
  recommendation: RecommendationDto;
  recommendationAttempts: RecommendationAttemptDto[];
  attemptAssessmentSubmissions: AssessmentSubmissionDto[];
  attemptFilter: ActivityProgressApplyScoreFilter;
}): RecommendationAttemptDto => {
  const {
    recommendation,
    recommendationAttempts,
    attemptAssessmentSubmissions,
    attemptFilter
  } = props;

  if (!recommendation || !recommendationAttempts || !recommendationAttempts.length) {
    return null;
  }

  if ([
    ActivityProgressApplyScoreFilter.AVG_ALL_ATTEMPTS,
    ActivityProgressApplyScoreFilter.AVG_FIRST_TWO_ATTEMPTS
  ].includes(attemptFilter)) {
    return null;
  }

  // Quiz attempts do not get assessment submissions, so we handle them separately
  // Quiz attempts do not need to be completed for instructor to view
  if (recommendation.contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
    return getQuizAttempt([recommendation], recommendationAttempts);
  }

  if (attemptFilter === ActivityProgressApplyScoreFilter.BEST_ATTEMPT) {
    return getBestCompletedAttempt({
      recommendation,
      recommendationAttempts,
      attemptAssessmentSubmissions
    });
  }

  if (attemptFilter === ActivityProgressApplyScoreFilter.FIRST_ATTEMPT) {
    return getFirstCompletedAttempt({
      recommendation,
      recommendationAttempts,
      attemptAssessmentSubmissions
    });
  }

  return null;
};

export const getScoreByFilterApply = (props: {
  recommendation: RecommendationDto;
  recommendationAttempts: RecommendationAttemptDto[];
  attemptAssessmentSubmissions: AssessmentSubmissionDto[];
  attemptFilter: ActivityProgressApplyScoreFilter;
}): number => {
  const {
    recommendation,
    recommendationAttempts,
    attemptAssessmentSubmissions,
    attemptFilter
  } = props;

  const attempts = getCompletedAttempts([recommendation], recommendationAttempts);

  if (!attempts || attempts.length === 0) {
    return null;
  }

  const submissions = sortAssessmentSubmissionsOldestToLatest(getAssessmentSubmissionsForAttempts(attempts, attemptAssessmentSubmissions));

  if (!submissions || submissions.length === 0) {
    return null;
  }

  if (submissions.length === 1 || attemptFilter === ActivityProgressApplyScoreFilter.FIRST_ATTEMPT) {
    return Math.round(submissions[0].score * 100);
  }

  if (attemptFilter === ActivityProgressApplyScoreFilter.BEST_ATTEMPT) {
    const bestAttempt = getBestCompletedAttempt({
      recommendationAttempts,
      attemptAssessmentSubmissions,
      recommendation
    });
    const bestAttemptSubmissions = getAssessmentSubmissionsForAttempts([bestAttempt], attemptAssessmentSubmissions);

    if (!bestAttemptSubmissions || bestAttemptSubmissions.length === 0) {
      return null;
    }

    return Math.round(bestAttemptSubmissions[0].score * 100);
  }

  if (attemptFilter === ActivityProgressApplyScoreFilter.AVG_ALL_ATTEMPTS) {
    const totalScore = submissions.reduce((total, {
      totalCorrect,
      totalAnswered
    }) =>
      total + (totalCorrect / totalAnswered), 0);

    return Math.round((totalScore / submissions.length) * 100);
  }

  // AVG_FIRST_TWO_ATTEMPTS
  return Math.round(((submissions[0].score + submissions[1].score) / 2) * 100);
};

export const showAttemptViewButton = (props: {
  recommendation: RecommendationDto;
  recommendationAttempts: RecommendationAttemptDto[];
  attemptAssessmentSubmissions: AssessmentSubmissionDto[];
  attemptFilter: ActivityProgressApplyScoreFilter;
}): boolean => {
  const {
    recommendation,
    recommendationAttempts,
    attemptAssessmentSubmissions,
    attemptFilter
  } = props;

  if (!recommendation || !recommendationAttempts || !recommendationAttempts.length) {
    return false;
  }

  if (recommendation.contentType === RecommendationContentTypeDto.HESI_CASE_STUDY) {
    // Case studies require completed attempts to view
    const attempt = getAttemptToViewByFilter({
      recommendation,
      recommendationAttempts,
      attemptAssessmentSubmissions,
      attemptFilter
    });
    return !!attempt;
  }

  return true;
};

export const isAnyRecommendationScored = (goalRecommendations: RecommendationDto[]): boolean => {
  if (!goalRecommendations) {
    return false;
  }

  return goalRecommendations.some((recommendation) => {
    const recContentType = recommendation.contentType;
    return RemediationContentTypeConfigMap[recContentType].isScored;
  });
};

export const getQuizStatusDisplay = (baseState: RemediationBaseState, assessRecommendation: RecommendationDto): JSX.Element => {
  if (!baseState || !assessRecommendation) {
    return null;
  }

  const status = getRecommendationStatusWithDueDate({
    baseState,
    recommendation: assessRecommendation
  });

  return (
    <StatusPill status={status}>
      {StatusDisplayMap[status]}
    </StatusPill>
  );
};

export const getCorrectOnFirstAttemptCount = (quizAttemptDetails: QuizAttemptDetailsDto): number => {
  if (!quizAttemptDetails || !quizAttemptDetails.questionSubmissionDetails || quizAttemptDetails.questionSubmissionDetails.length === 0) {
    return null;
  }

  const allQuestions = quizAttemptDetails.questionSubmissionDetails.flatMap(detail => detail.questions || []);

  const firstAttempts = allQuestions.filter(question => {
    const questionTime = new Date(question.responseTime).getTime();

    return !allQuestions.some(q =>
      q.questionVtwId === question.questionVtwId
      && q.responseTime
      && new Date(q.responseTime).getTime() < questionTime);
  });

  return firstAttempts.filter(q => q.correct).length;
};

export const convertDateToMMDDYYYY = (date: Date): string => {
  if (!date) {
    return null;
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};
export const convertDateToMMDDYYYYHHMM = (date: Date): string => {
  if (!date) {
    return null;
  }

  const dateString = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${dateString} ${timeString}`;
};

export const isSingleSourceAssessment = (sourceList: RaasAssessmentSourceDto[]): boolean => {
  return sourceList && sourceList.length === 1;
};

export const isAssessmentHesiSource = (sourceList: RaasAssessmentSourceDto[]): boolean => {
  if (!sourceList || !Array.isArray(sourceList)) {
    return false;
  }

  return sourceList && isSingleSourceAssessment(sourceList)
    && sourceList.some(source => source.sourceType === RaasAssessmentSourceTypeDto.HESI_USER_PERFORMANCE_EXAM || source.sourceType === RaasAssessmentSourceTypeDto.HESI_USER_PERFORMANCE_EXAM__TESTTAKER_GUID);
};

export const isAssignmentHesiSource = (sourceList: RemediationAssignmentWithSourcesDto['sourceList']): boolean => {
  if (!sourceList || !Array.isArray(sourceList)) {
    return false;
  }

  return sourceList && sourceList.length === 1 && sourceList.some(source => source.sourceType === RemediationAssignmentSourceTypeDto.HESI_EXAM_EDITION__EDITION_ID);
};

export const getAssessmentDropdownOptions = (
  assignmentWithSources: RemediationAssignmentWithSourcesDto,
  activeRaasAssessments: RaasAssessmentDto[],
  usersByIdDto: Record<string, UserDto>
): ELSDropDownOption[] => {
  if (!activeRaasAssessments || !activeRaasAssessments.length || !usersByIdDto) {
    return [];
  }

  const sourceList = assignmentWithSources?.sourceList;
  const isHesiSource = isAssignmentHesiSource(sourceList);
  const assessmentDistinguisher = isHesiSource ? 'Exam date taken:' : 'Created on:';

  return usersByIdDto && activeRaasAssessments.map((assessment: RaasAssessmentDto): ELSDropDownOption => {
    const user = usersByIdDto[assessment.userId];
    const isRepeatedUser = activeRaasAssessments.filter(a => a.userId === assessment.userId).length > 1;

    return {
      name: user && isRepeatedUser
        ? `${user.lastName}, ${user.firstName} (${assessmentDistinguisher} ${convertDateToMMDDYYYY(new Date(assessment.createdAt))})`
        : `${user.lastName}, ${user.firstName}`,
      value: assessment.id.toString()
    };
  })
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
};

export const isRecommendationInProgress = (recommendation: RecommendationDto, baseState: RemediationBaseState): boolean => {
  if (!recommendation || !baseState) {
    return false;
  }

  const status = getRecommendationStatusWithDueDate({
    recommendation,
    baseState,
  });

  return status === StatusWithDueDate.IN_PROGRESS;
};

export const getActivityLogTimeSpentDisplay = (props: {
  activityLogRow: ActivityLogTableData;
  baseState: RemediationBaseState;
}): string => {
  const {
    activityLogRow,
    baseState
  } = props;

  const timeSpentSeconds = getActivityTimeSpentSeconds(activityLogRow, baseState.attemptAssessmentSubmissions);

  if (isRecommendationInProgress(activityLogRow.recommendation, baseState)
    && timeSpentSeconds === 0
    && activityLogRow.recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
    return EmptyValueDisplay;
  }

  return getHourAndMinuteFromSecond(timeSpentSeconds);
};

export const getRecommendationTimeSpentDisplay = (props: {
  recommendation: RecommendationDto;
  baseState: RemediationBaseState;
}): string => {
  const {
    recommendation,
    baseState
  } = props;

  const timeSpentSeconds = getRecTimeSpent(recommendation, baseState.recommendationAttempts, baseState.attemptAssessmentSubmissions);

  if (isRecommendationInProgress(recommendation, baseState) && timeSpentSeconds === 0) {
    return EmptyValueDisplay;
  }

  return getHourAndMinuteFromSecond(timeSpentSeconds);
};
